import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import { cn } from "@/lib/utils";
import { FilterIcon } from "@assets/icons";
import { Badge } from "@components/ui/badge";
import { Button } from "@components/ui/button";
import { Checkbox } from "@components/ui/checkbox";
import { Input } from "@components/ui/input";
import { Skeleton } from "@components/ui/skeleton";
import { useDebounce, useInfiniteScroller } from "@pages/dashboard/test/hook";
import { endpoint } from "@services/api.services";
import { SelectProps } from "@services/interface.service";
import Tooltip from "@ui/external/components/tooltip";
import { formatSelectData } from "@utils/helper/index.helper";
import { usePathname } from "@utils/hook/index.hook";
import { ArrowLeft, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import { useSearchParams } from "react-router";

interface FilterOption {
  label: string;
  value: string;
}

interface FilterConfig {
  label?: string;
  endpointValue?: string;
  searchKey?: string;
  format?: { key: string; value: string };
  defaultOptions?: FilterOption[];
  radio?: boolean;
  queries?: {
    [key: string]: string;
  };
}

interface FilterComponentProps {
  triggerText?: string;
  title?: string;
  filters: FilterConfig[];
}

const FilterPopover = ({
  triggerText,
  title,
  filters,
}: FilterComponentProps) => {
  const [searchValue, setSearchValue] = useState("");
  const [endpointValue, setEndpointValue] = useState<string>("");
  const [format, setFormat] = useState({ key: "title", value: "id" });
  const [slideSidebarOpen, setSlideSidebarOpen] = useState(false);
  const [currentFilterKey, setCurrentFilterKey] = useState("");
  const [additionalQueries, setAdditionalQueries] = useState<any>();
  const pathname = usePathname();
  const debouncedValue = useDebounce({
    initalValue: searchValue,
    delay: 500,
  });
  // console.log("Endpoint Value", endpointValue);

  const { data, isFetchingNextPage, fetchNextPage, hasNextPage, isPending } =
    useInfiniteScroller({
      endpoint: endpoint[`${endpointValue}`],
      filter: {
        search: debouncedValue,
        ...additionalQueries,
      },
      key: endpointValue,
      enabled:
        !!endpointValue &&
        !filters.find((f) => f.endpointValue === endpointValue)?.defaultOptions,
    });

  const { inView, ref } = useInView();
  const opts = data?.pages?.flatMap((item: any) => item?.results) || [];
  const options = formatSelectData<SelectProps>(opts, format.key, format.value, "");

  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentFilter, setCurrentFilter] = useState<FilterConfig | null>(null);

  useEffect(() => {
    if (inView && hasNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, fetchNextPage]);

  const activeFilter =
    filters
      .map((item) =>
        searchParams.get(item.endpointValue || item?.searchKey || "")
      )
      .filter(Boolean).length > 0;

  const handleFilterClick = (filter: FilterConfig) => {
    setSlideSidebarOpen(true);
    setCurrentFilter(filter);

    if (filter.format) {
      setFormat(filter.format);
    }

    if (filter.endpointValue) {
      setEndpointValue(filter.endpointValue);
    }

    /* always reassign — leaving a stale key behind hides the search box on the
       endpoint-backed filters that actually need it */
    setCurrentFilterKey(filter.searchKey || "");
    if (filter.queries) {
      setAdditionalQueries(filter.queries);
    }
    // Sync selectedOptions from current searchParams
    const paramKey = filter.endpointValue || filter.searchKey || "";
    const selectedFromParams =
      searchParams.get(paramKey)?.split(",").filter(Boolean) || [];
    setSelectedOptions(selectedFromParams);
  };

  const handleApplyFilters = () => {
    if (!currentFilter) return;

    const paramKey =
      currentFilter.endpointValue || currentFilter.searchKey || "";
    if (selectedOptions.length > 0) {
      console.log("this is selectedOptions", selectedOptions.join(","));
      if (paramKey == "course-batch-with-invoices") {
        searchParams.set("course-batch", selectedOptions.join(","));
      } else {
        searchParams.set(paramKey, selectedOptions.join(","));
      }
    } else {
      searchParams.delete(paramKey);
    }

    searchParams.set("tpage", "1");
    setSearchParams(searchParams);
    setSlideSidebarOpen(false);
    setSearchValue("");
    setEndpointValue("");
  };

  const resetAllFilters = () => {
    setSelectedOptions([]);
    setSearchValue("");
    setEndpointValue("");
    filters.forEach((item) => {
      const key = item.endpointValue || item.searchKey;
      if (key) {
        searchParams.delete(key);
      }
    });
    setSearchParams(searchParams);
  };

  const toggleOption = (value: string) => {
    if (currentFilter?.radio) {
      setSelectedOptions(
        (prev) => (prev.includes(value) ? [] : [value]) // deselect if clicked again
      );
    } else {
      setSelectedOptions((prev) =>
        prev.includes(value)
          ? prev.filter((v) => v !== value)
          : [...prev, value]
      );
    }
  };

  const renderOptions = () => {
    if (currentFilter?.defaultOptions) {
      const filteredOptions = currentFilter.defaultOptions.filter((item) =>
        String(item.label).toLowerCase().includes(debouncedValue.toLowerCase())
      );

      if (filteredOptions.length === 0) {
        return (
          <li className="p-4 text-center">
            No results found for "{debouncedValue}"
          </li>
        );
      }

      return currentFilter.defaultOptions.map((item) => {
        const isSelected = selectedOptions.includes(String(item.value));

        return (
          <li
            key={item.value}
            onClick={() => toggleOption(String(item.value))}
            className="my-2 cursor-pointer flex items-center p-4 border-0 border-b-2 justify-between"
          >
            <span className="w-[200px] truncate">{item.label || "N/A"}</span>

            {currentFilter.radio ? (
              <div
                className={`w-4 h-4 rounded-full border-2 transition-all duration-200 ${
                  isSelected
                    ? "bg-[#1aa178] border-[#1aa178]"
                    : "border-gray-400"
                }`}
              />
            ) : (
              <Checkbox checked={isSelected} />
            )}
          </li>
        );
      });
    }

    return (
      <>
        {options?.map((item: SelectProps, i: number) => (
          <li
            key={i}
            onClick={() => toggleOption(String(item.value))}
            className="my-2 cursor-pointer flex items-center p-4 border-0 border-b-2 justify-between"
          >
            <Tooltip description={item?.label}>
              <div className="flex items-start justify-start flex-col gap-2">
                <span className="w-[200px] truncate">
                  {item?.label || "N/A"}
                </span>
                {opts.find((opt) => opt.id === item.value)?.user?.full_name ? (
                  <Badge className="cursor-pointer text-white bg-[#1aa178] transition-all">
                    {opts.find((opt) => opt.id === item.value)?.user?.full_name}
                  </Badge>
                ) : (
                  opts.find((opt) => opt.id === item.value)?.email && (
                    <Badge className="cursor-pointer text-white bg-[#1aa178] transition-all">
                      {opts.find((opt) => opt.id === item.value)?.email}
                    </Badge>
                  )
                )}
              </div>
            </Tooltip>

            {currentFilter?.radio ? (
              <div
                className={`w-4 h-4 rounded-full border-2 ${
                  selectedOptions.includes(String(item.value))
                    ? "bg-[#1aa178] border-[#1aa178]"
                    : "border-gray-400"
                }`}
              />
            ) : (
              <Checkbox
                checked={selectedOptions.includes(String(item.value))}
              />
            )}
          </li>
        ))}
        <div ref={ref} style={{ textAlign: "center", padding: "1rem" }}>
          {isFetchingNextPage || isPending ? (
            <div className="flex flex-col gap-10 w-full">
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className="flex gap-2 justify-between items-center"
                >
                  <Skeleton className="w-full h-[30px]"></Skeleton>
                  <Skeleton className="w-[20px] h-[20px]"></Skeleton>
                </div>
              ))}
            </div>
          ) : hasNextPage ? (
            "Scroll to load more Results"
          ) : options.length == 0 ? (
            "✅ No Results"
          ) : (
            `✅ No more results`
          )}
        </div>
      </>
    );
  };

  return (
    <Drawer direction="right">
      <DrawerTrigger>
        <Button variant={activeFilter ? "destructive" : "outline"}>
          <FilterIcon color={activeFilter ? "#fff" : ""} />
          {activeFilter ? "Filter Active" : triggerText || "Filter"}
        </Button>
      </DrawerTrigger>
      <DrawerContent className="px-[20px] py-[30px]">
        {slideSidebarOpen ? (
          <>
            <div className="w-full p-[20px] rounded-md h-[40vh] sm:max-w-sm">
              <div className="flex w-full items-end justify-between">
                <Button
                  onClick={() => {
                    setSlideSidebarOpen(false);
                    setSearchValue("");
                  }}
                  variant={"ghost"}
                  className="border mb-[20px]"
                >
                  <ArrowLeft />
                </Button>
                <DrawerClose>
                  <Button
                    onClick={() => setSearchValue("")}
                    variant={"ghost"}
                    className="border mb-[20px]"
                  >
                    <X />
                  </Button>
                </DrawerClose>
              </div>
              {!currentFilterKey && (
                <div className="w-full">
                  <Input
                    type="text"
                    placeholder="Search"
                    className="my"
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                  />
                </div>
              )}

              <div className="my-4 h-[400px] overflow-y-auto">
                <ul>{renderOptions()}</ul>
              </div>
            </div>
          </>
        ) : (
          <>
            <DrawerHeader>
              <DrawerClose>
                <Button
                  onClick={() => setSearchValue("")}
                  variant={"ghost"}
                  className="border flex justify-end mb-[20px]"
                >
                  <X />
                </Button>
              </DrawerClose>
              <DrawerTitle className="capitalize">
                {title || "Filter"} {String(pathname).replace("-", " ")} by
              </DrawerTitle>
              <DrawerDescription className="capitalize">
                Filter your search by selecting specific{" "}
                {String(pathname).replace("-", " ")} types.
              </DrawerDescription>
            </DrawerHeader>
            <aside className="flex flex-col h-[400px] overflow-y-auto gap-4">
              {filters.map((item) => {
                const paramKey = item.endpointValue || item.searchKey || "";
                const paramValue = searchParams.get(paramKey);
                const selectedCount =
                  paramValue?.split(",").filter(Boolean).length || 0;

                return (
                  <div
                    key={item.label}
                    className={cn(
                      "h-[77px] flex flex-col items-start justify-center px-[20px] rounded-md",
                      selectedCount > 0
                        ? "bg-[#F5F5F5] border border-[#E0E0E0]"
                        : "bg-white border border-[#E0E0E0] hover:bg-[#F5F5F5] hover:border-[#C4C4C4]"
                    )}
                    onClick={() => handleFilterClick(item)}
                  >
                    {item.label}
                    {selectedCount > 0 && (
                      <span className="text-xs">
                        {selectedCount} {item.label || "Categories"} selected
                      </span>
                    )}
                  </div>
                );
              })}
            </aside>
          </>
        )}
        <DrawerFooter>
          <div className="flex bg-white justify-center items-center gap-4">
            <DrawerClose>
              {activeFilter && (
                <Button
                  variant="outline"
                  className="text-[#C33241] font-medium border"
                  onClick={() => {
                    resetAllFilters();
                    window.location.reload();
                  }}
                >
                  Reset All Filters
                </Button>
              )}
            </DrawerClose>
            {slideSidebarOpen && (
              <Button
                className="flex items-end justify-end"
                onClick={handleApplyFilters}
              >
                Apply
              </Button>
            )}
          </div>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default FilterPopover;
